import React, { useState } from 'react';

import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem } from '@mui/material';

import LensSchedulesDialog from '~/pages/LeLens/schedules/LensSchedulesDialog';
import PrintSettingsFields from '~/pages/LeLens/print/PrintSettingsFields';
import { DEFAULT_PRINT_SETTINGS, type PrintSettings } from '~/pages/LeLens/print/printSettings';

import { useGetReportingLeLens } from '~/hooks/apis/reporting/useGetReportingLeLens';
import useCurrentUser from '~/hooks/useCurrentUser';

import type { LensFilterValues, LensPeriod } from '~/types/leLens';

import { nativeLensId } from './nativeLensId';

type Props = {
  pageId: string;
  filters: LensFilterValues;
  filterOptions: Record<string, Array<string>>;
  ready: boolean;
  onDownloadPdf: (settings?: PrintSettings) => void;
  initialPeriod?: LensPeriod | null;
  allowScheduling?: boolean;
};

export default function NativeLensActions({
  pageId,
  filters,
  filterOptions,
  ready,
  onDownloadPdf,
  initialPeriod,
  allowScheduling = true,
}: Props) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [printOpen, setPrintOpen] = useState(false);
  const [print, setPrint] = useState<PrintSettings>(DEFAULT_PRINT_SETTINGS);
  const { user } = useCurrentUser();
  const query = useGetReportingLeLens(open ? nativeLensId(pageId) : undefined);
  return (
    <>
      <IconButton aria-label="Report actions" aria-haspopup="menu" onClick={(event) => setAnchor(event.currentTarget)}>
        <MoreHorizIcon />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <MenuItem
          disabled={!ready}
          onClick={() => {
            setAnchor(null);
            setPrintOpen(true);
          }}
        >
          Download PDF
        </MenuItem>
        {allowScheduling && (
          <MenuItem
            disabled={!ready}
            onClick={() => {
              setAnchor(null);
              setOpen(true);
            }}
          >
            Schedule email…
          </MenuItem>
        )}
      </Menu>
      <Dialog open={printOpen} onClose={() => setPrintOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Download PDF</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <PrintSettingsFields value={print} onChange={setPrint} showImage={false} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrintOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!ready} onClick={() => {
            setPrintOpen(false);
            onDownloadPdf(print);
          }}>
            Download PDF
          </Button>
        </DialogActions>
      </Dialog>
      {open && query.data ? (
        <LensSchedulesDialog
          open
          onClose={() => setOpen(false)}
          lens={query.data.lens}
          screenValues={filters}
          filterOptions={filterOptions}
          userEmail={user?.email}
          allowPeriodOverride={pageId !== 'daily-snapshot'}
          initialPeriod={initialPeriod ?? (filters.period as LensPeriod | undefined)}
        />
      ) : (
        <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>Schedule email</DialogTitle>
          <DialogContent>
            {query.isError ? (
              <Alert severity="error">
                {query.error?.message ?? 'The report could not load. Check its Library registration and try again.'}
              </Alert>
            ) : (
              <CircularProgress size={24} aria-label="Loading report schedules" />
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
